import React, { createContext, useState, useContext, useEffect } from 'react';
import onboardingService from '../services/onboardingService';

const OnboardingContext = createContext(null);

export const useOnboarding = () => {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error('useOnboarding must be used within an OnboardingProvider');
  }
  return context;
};

export const OnboardingProvider = ({ children }) => {
  const [personalityProfile, setPersonalityProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasCompletedOnboarding, setHasCompletedOnboarding] = useState(false);
  const [currentSession, setCurrentSession] = useState(null);
  const [isOnboardingActive, setIsOnboardingActive] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  // Load personality profile on mount
  useEffect(() => {
    const loadPersonalityProfile = async () => {
      try {
        console.log('🎯 OnboardingContext: Loading personality profile on app start');
        setIsInitialLoading(true);
        
        // First check completion flag as immediate indicator
        const completionFlag = onboardingService.getCompletionFlag();
        
        // Then try cached version for immediate feedback
        const cachedProfile = onboardingService.getCachedPersonalityProfile();
        if (cachedProfile) {
          console.log('🎯 Using cached personality profile');
          setPersonalityProfile(cachedProfile);
          setHasCompletedOnboarding(true);
        } else if (completionFlag) {
          console.log('🎯 No cached profile but completion flag indicates completed');
          setHasCompletedOnboarding(true);
        }

        // Then fetch fresh version from API
        const profileData = await onboardingService.getPersonalityProfile();
        if (profileData.success && profileData.data.personalityProfile) {
          console.log('🎯 Fresh personality profile loaded:', profileData.data);
          setPersonalityProfile(profileData.data.personalityProfile);
          setHasCompletedOnboarding(profileData.data.onboardingCompleted);
          
          // Cache the fresh data
          onboardingService.cachePersonalityProfile(profileData.data.personalityProfile);
        }
      } catch (error) {
        if (error.message === 'PROFILE_NOT_FOUND') {
          console.log('🎯 No personality profile found via API');
          
          // Only reset completion state if we also don't have cached data AND no completion flag
          const cachedProfile = onboardingService.getCachedPersonalityProfile();
          const completionFlag = onboardingService.getCompletionFlag();
          
          if (!cachedProfile && !completionFlag) {
            console.log('🎯 No cached profile and no completion flag - user needs onboarding');
            setPersonalityProfile(null);
            setHasCompletedOnboarding(false);
          } else {
            console.log('🎯 API failed but cached profile or completion flag exists - keeping completed status');
            // Keep the cached profile and completion status as-is
            // Don't override hasCompletedOnboarding that was set from cache/flag
          }
        } else {
          console.error('🎯 Error loading personality profile:', error);
          // For other errors (network, auth, etc.), don't change completion state
          // The cached data should remain valid
        }
      } finally {
        setIsInitialLoading(false);
        console.log('🎯 Initial profile loading completed');
      }
    };

    // Small delay to ensure authentication is loaded first
    const timer = setTimeout(loadPersonalityProfile, 500);
    return () => clearTimeout(timer);
  }, []);

  // Start onboarding session
  const startOnboarding = async () => {
    try {
      setError(null);
      setLoading(true);
      console.log('OnboardingContext: Starting onboarding...');
      
      const response = await onboardingService.startOnboarding();
      if (response.success) {
        setCurrentSession({
          sessionId: response.data.sessionId,
          personalityId: response.data.personalityId,
          currentQuestion: response.data.firstQuestion,
          questionCount: 1
        });
        setIsOnboardingActive(true);
        console.log('OnboardingContext: Onboarding session started:', response.data);
        return { success: true, data: response.data };
      } else {
        throw new Error(response.message || 'Failed to start onboarding');
      }
    } catch (error) {
      console.error('OnboardingContext: Error starting onboarding:', error);
      
      if (error.message === 'ALREADY_COMPLETED') {
        setError('You have already completed the personality assessment!');
        return { success: false, error: 'ALREADY_COMPLETED' };
      }
      
      setError(error.message || 'Failed to start onboarding');
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  // Start onboarding from chat with context
  const startOnboardingFromChat = async (conversationContext) => {
    try {
      setError(null);
      setLoading(true);
      console.log('OnboardingContext: Starting onboarding from chat...', conversationContext);
      
      const response = await onboardingService.startOnboardingFromChat(conversationContext);
      if (response.success) {
        setCurrentSession({
          sessionId: response.data.sessionId,
          personalityId: response.data.personalityId,
          currentQuestion: response.data.firstQuestion,
          questionCount: 1,
          fromChat: response.data.fromChat,
          originalContext: response.data.originalContext,
          introMessage: response.data.introMessage
        });
        setIsOnboardingActive(true);
        console.log('OnboardingContext: Chat onboarding session started:', response.data);
        return { success: true, data: response.data };
      } else {
        throw new Error(response.message || 'Failed to start chat onboarding');
      }
    } catch (error) {
      console.error('OnboardingContext: Error starting chat onboarding:', error);
      
      if (error.message === 'ALREADY_COMPLETED') {
        // User already completed, redirect to continue chat
        if (error.redirectTo === 'CONTINUE_CHAT') {
          setError(null);
          return { success: false, error: 'ALREADY_COMPLETED', redirectTo: 'CONTINUE_CHAT' };
        }
      }
      
      setError(error.message || 'Failed to start chat onboarding');
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  // Submit response and get next question
  const submitResponse = async (response) => {
    if (!currentSession) {
      throw new Error('No active onboarding session');
    }

    try {
      setError(null);
      setLoading(true);
      console.log('OnboardingContext: Submitting response:', response);
      
      const apiResponse = await onboardingService.submitResponse(currentSession.sessionId, response);
      
      if (apiResponse.success) {
        if (apiResponse.data.completed) {
          // Onboarding completed!
          console.log('OnboardingContext: Onboarding completed!', apiResponse.data);
          setPersonalityProfile(apiResponse.data.personalityProfile);
          setHasCompletedOnboarding(true);
          setIsOnboardingActive(false);
          setCurrentSession(null);
          
          // Cache the personality profile and set completion flag
          onboardingService.cachePersonalityProfile(apiResponse.data.personalityProfile);
          
          return { 
            success: true, 
            completed: true, 
            personalityProfile: apiResponse.data.personalityProfile 
          };
        } else {
          // Continue to next question
          setCurrentSession(prev => ({
            ...prev,
            currentQuestion: apiResponse.data.nextQuestion,
            questionCount: prev.questionCount + 1,
            insights: apiResponse.data.insights
          }));
          
          return { 
            success: true, 
            completed: false, 
            nextQuestion: apiResponse.data.nextQuestion,
            insights: apiResponse.data.insights
          };
        }
      } else {
        throw new Error(apiResponse.message || 'Failed to submit response');
      }
    } catch (error) {
      console.error('OnboardingContext: Error submitting response:', error);
      setError(error.message || 'Failed to submit response');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Reset personality profile
  const resetPersonalityProfile = async () => {
    try {
      setError(null);
      setLoading(true);
      console.log('OnboardingContext: Resetting personality profile...');
      
      const response = await onboardingService.resetPersonalityProfile();
      if (response.success) {
        setPersonalityProfile(null);
        setHasCompletedOnboarding(false);
        setIsOnboardingActive(false);
        setCurrentSession(null);
        
        // Clear cached profile
        onboardingService.clearCachedPersonalityProfile();
        
        console.log('OnboardingContext: Personality profile reset successfully');
        return { success: true };
      } else {
        throw new Error(response.message || 'Failed to reset profile');
      }
    } catch (error) {
      console.error('OnboardingContext: Error resetting profile:', error);
      setError(error.message || 'Failed to reset profile');
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  // Update adaptive profile based on behavior
  const updateAdaptiveProfile = async (behaviorData) => {
    try {
      console.log('OnboardingContext: Updating adaptive profile...', behaviorData);
      
      const response = await onboardingService.updateAdaptiveProfile(behaviorData);
      if (response.success) {
        // Refresh personality profile to get updated adaptive data
        const updatedProfile = await onboardingService.getPersonalityProfile();
        if (updatedProfile.success) {
          setPersonalityProfile(updatedProfile.data.personalityProfile);
          onboardingService.cachePersonalityProfile(updatedProfile.data.personalityProfile);
        }
        
        console.log('OnboardingContext: Adaptive profile updated successfully');
        return { success: true };
      }
    } catch (error) {
      console.error('OnboardingContext: Error updating adaptive profile:', error);
      // Don't show error to user for adaptive updates - they're background
      return { success: false, error: error.message };
    }
  };

  // Cancel active onboarding session
  const cancelOnboarding = () => {
    console.log('OnboardingContext: Cancelling onboarding session');
    setCurrentSession(null);
    setIsOnboardingActive(false);
    setError(null);
  };

  // Get risk tolerance category for UI
  const getRiskCategory = () => {
    if (!personalityProfile?.riskTolerance) return 'unknown';
    return personalityProfile.riskTolerance.category || 'moderate';
  };

  // Get learning style for UI adaptation
  const getLearningStyle = () => {
    if (!personalityProfile?.learningStyle) return null;
    return personalityProfile.learningStyle;
  };

  // Check if user needs encouragement based on profile
  const needsEncouragement = () => {
    if (!personalityProfile?.learningStyle) return false;
    return personalityProfile.learningStyle.needsEncouragement || false;
  };

  const value = {
    // State
    personalityProfile,
    loading,
    error,
    hasCompletedOnboarding,
    currentSession,
    isOnboardingActive,
    isInitialLoading,
    
    // Actions
    startOnboarding,
    startOnboardingFromChat,
    submitResponse,
    resetPersonalityProfile,
    updateAdaptiveProfile,
    cancelOnboarding,
    
    // Utilities
    getRiskCategory,
    getLearningStyle,
    needsEncouragement,
    
    // Clear error
    clearError: () => setError(null)
  };

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
};